'use client';

import * as React from 'react';
import { PieChart as MuiPieChart, PieChartProps as MuiPieChartProps } from '@mui/x-charts/PieChart';
import { Menu, MenuItem } from '@mui/material';

export interface PieChartWrapperProps extends MuiPieChartProps {
  // Future: qui possiamo aggiungere props custom per altre librerie
  unitOfMeasurement?: string; // Unità di misura da mostrare nella leggenda
}

/**
 * Wrapper per PieChart di MUI X Charts.
 * Questo componente permette di cambiare facilmente la libreria di grafici in futuro
 * senza dover modificare tutti i file che lo utilizzano.
 */
export const PieChartWrapper: React.FC<PieChartWrapperProps> = props => {
  const { unitOfMeasurement, series, slotProps, ...restProps } = props;
  const chartRef = React.useRef<HTMLDivElement>(null);

  const [menuPos, setMenuPos] = React.useState<{ mouseX: number; mouseY: number } | null>(null);

  // Processa le serie per aggiungere la funzione label custom quando unitOfMeasurement è fornita
  const processedSeries = React.useMemo(() => {
    if (!unitOfMeasurement || !series) {
      return series;
    }

    return series.map((serie: any) => {
      const serieData = Array.isArray(serie.data) ? serie.data : [];

      // Calcola il totale per questa serie
      const total = serieData.reduce((sum: number, item: any) => {
        const value = typeof item === 'object' && item !== null ? item.value : item;
        return sum + (typeof value === 'number' ? value : 0);
      }, 0);

      // Processa i dati aggiungendo la funzione label
      const processedData = serieData.map((item: any) => {
        const originalLabel =
          typeof item === 'object' && item !== null
            ? typeof item.label === 'function'
              ? item.label
              : item.label || item.id || ''
            : '';
        const value = typeof item === 'object' && item !== null ? item.value : item;
        const percentage = total > 0 ? ((value as number) / total) * 100 : 0;

        const formattedValue =
          value !== undefined && typeof value === 'number'
            ? serie.valueFormatter({ value })
            : String(value);

        // Se l'item ha già una funzione label, la preserviamo e la estendiamo
        if (typeof item === 'object' && item !== null && typeof item.label === 'function') {
          return {
            ...item,
            label: (location: string) => {
              if (location === 'legend') {
                return `${originalLabel} ${formattedValue} ${unitOfMeasurement} (${percentage.toFixed(1)}%)`;
              }
              // Per altre location, usa la funzione originale
              return item.label(location);
            },
          };
        }

        // Altrimenti crea una nuova funzione label
        return {
          ...item,
          label: (location: string) => {
            if (location === 'legend') {
              return `${originalLabel} - ${formattedValue} ${unitOfMeasurement} (${percentage.toFixed(1)}%)`;
            }
            // Per altre location, restituisci il label originale
            return originalLabel;
          },
        };
      });

      return {
        ...serie,
        data: processedData,
      };
    });
  }, [series, unitOfMeasurement]);

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    setMenuPos({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
  };

  const handleClose = () => setMenuPos(null);

  // Funzione comune per ottenere l'SVG clonato con gli stili applicati
  const getSvg = (): SVGSVGElement | null => {
    const svg = chartRef.current?.querySelector('svg');
    if (!svg) {
      console.error('SVG non trovato');
      return null;
    }

    // Clona l'SVG per non modificare il DOM originale
    const svgClone = svg.cloneNode(true) as SVGSVGElement;

    // Raccoglie tutti i CSS attivi nel documento
    let css = '';
    for (const sheet of Array.from(document.styleSheets)) {
      try {
        for (const rule of Array.from(sheet.cssRules)) {
          css += rule.cssText;
        }
      } catch (e) {
        // cross-origin sheet, ignora
      }
    }

    // Applica CSS globali e aggiunge override per assi e label neri
    css += `
      .MuiChartsAxis-line,
      .MuiChartsAxis-tick,
      .MuiChartsAxis-label,
      .MuiChartsAxis-tickLabel {
        color: #000 !important;
      }
      .MuiChartsLegend-root,
      .MuiChartsLegend-root *,
      .MuiChartsLegend-root text,
      .MuiChartsLegend-root tspan {
        color: #000 !important;
      }
      text{
        fill: inherit !important;
      }
    `;

    const styleElement = document.createElement('style');
    styleElement.textContent = css;
    svgClone.insertBefore(styleElement, svgClone.firstChild);

    return svgClone;
  };

  const handleExportSvg = () => {
    const svgClone = getSvg();
    if (!svgClone) return;

    // Serializza e scarica
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${props.title ?? 'chart'}.svg`;
    link.click();
    URL.revokeObjectURL(link.href);
    handleClose();
  };

  const handleExportPng = () => {
    const svgClone = getSvg();
    if (!svgClone) return;

    // Serializza SVG
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgClone);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    // Crea un'immagine dall'SVG
    const img = new Image();
    img.onload = () => {
      // Crea un canvas e disegna l'immagine
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        return console.error('Impossibile creare il contesto del canvas');
      }

      // Sfondo bianco
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Disegna l'immagine
      ctx.drawImage(img, 0, 0);

      // Converti in PNG e scarica
      canvas.toBlob(blob => {
        if (!blob) {
          URL.revokeObjectURL(url);
          return console.error('Impossibile creare il blob PNG');
        }
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${props.title ?? 'chart'}.png`;
        link.click();
        URL.revokeObjectURL(link.href);
        URL.revokeObjectURL(url);
        handleClose();
      }, 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      console.error("Errore nel caricamento dell'immagine SVG");
    };
    img.src = url;
  };

  return (
    <div ref={chartRef} onContextMenu={handleContextMenu} style={{ display: 'inline-block' }}>
      <MuiPieChart {...restProps} series={processedSeries} slotProps={slotProps} />

      <Menu
        open={menuPos !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={menuPos ? { top: menuPos.mouseY, left: menuPos.mouseX } : undefined}
      >
        <MenuItem onClick={handleExportSvg}>Scarica SVG</MenuItem>
        <MenuItem onClick={handleExportPng}>Scarica PNG</MenuItem>
      </Menu>
    </div>
  );
};

// Esporta anche le classi CSS utili per la personalizzazione
export { pieArcLabelClasses } from '@mui/x-charts/PieChart';
